import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag } from "lucide-react";
import { api } from "@/api/client";
import { StatusBadge } from "@/components/StatusTimeline";
import { money } from "@/lib/pricing";
import { getTheme } from "@/lib/theme";

export default function MyOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active");

  useEffect(() => {
    api.lookupOrders({ mine: true }).then(setOrders).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const active = orders.filter((o) => !["completed", "rejected"].includes(o.status));
  const past = orders.filter((o) => ["completed", "rejected"].includes(o.status));
  const shown = filter === "active" ? active : past;

  return (
    <div className="container-app py-8 sm:py-12">
      <h1 className="font-display text-4xl" style={{ color: "var(--ink)" }}>My orders</h1>

      <div className="mt-6 flex gap-2">
        {[
          { k: "active", label: `Active (${active.length})` },
          { k: "past", label: `Past (${past.length})` },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setFilter(t.k)}
            data-testid={`orders-filter-${t.k}`}
            className="px-4 py-2 rounded-full text-sm font-semibold transition"
            style={filter === t.k ? { background: "var(--brand)", color: "var(--brand-fg)" } : { background: "var(--surface)", color: "var(--muted)", border: "1px solid var(--border)" }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-8 space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-24 rounded-2xl animate-pulse" style={{ background: "var(--surface-2)" }} />
          ))}
        </div>
      ) : shown.length === 0 ? (
        <div className="mt-10 text-center py-16 rounded-2xl" style={{ background: "var(--surface)", border: "1px dashed var(--border)" }}>
          <ShoppingBag className="mx-auto mb-3" style={{ color: "var(--muted)" }} size={28} />
          <p className="font-display text-xl" style={{ color: "var(--ink)" }}>No {filter} orders</p>
          <Link to="/shop" className="btn-brand inline-flex mt-5 px-6 py-2.5 rounded-full font-semibold">Start an order</Link>
        </div>
      ) : (
        <div className="mt-8 space-y-3">
          {shown.map((o) => {
            const t = getTheme(o.business_id);
            const paidPill = o.balance_paid ? "Paid" : o.deposit_paid ? "Deposit paid" : "Pay pending";
            return (
              <Link
                key={o.id}
                to={`/order/${o.id}`}
                data-testid={`myorder-${o.id}`}
                className="flex items-center justify-between gap-4 rounded-2xl p-5 card-lift"
                style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-display text-lg" style={{ color: "var(--ink)" }}>Order {o.order_number}</span>
                    <StatusBadge status={o.status} />
                  </div>
                  <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>
                    {t.name} · {o.items.length} item{o.items.length > 1 ? "s" : ""} · {o.pickup_date}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-lg" style={{ color: "var(--brand)" }}>{money(o.total)}</div>
                  <div className="text-xs font-semibold" style={{ color: o.balance_paid ? "#12633a" : "var(--muted)" }}>{paidPill}</div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
